// reportBodyPdf.js — MVP: render the on-screen ReportDocument body to A4 page images (html2canvas).
// A vector body comes in Phase 2 (server-side headless render); for MVP the body is a small raster (a few
// pages), which stays drift-free (it IS the rendered HTML) with no new infrastructure. Appendices are NEVER
// handled here — they are merged as native PDF pages in reportPackage.js.
//
// html2canvas is loaded LAZILY (dynamic import) only when generation actually runs, so it never bloats the
// main app bundle for users who don't generate packages.
//
// PAGE-BREAK-AWARE SLICING — the tall html2canvas canvas is cut into pages at "safe" rows (rows that contain
// no ink: no text glyphs, no dark section headers). Cutting only on whitespace/border rows means a page break
// never bisects a table row or clips a dark header — which was the root cause of the mid-content "black bars"
// (a guillotined dark header sat flush at a page edge, and split rows appeared duplicated across the seam).

// A4 in PDF points (pdf-lib's unit): 595.28 × 841.89.
export const A4 = { w: 595.28, h: 841.89 }

// Capture `element` and slice the tall canvas into A4-height page images, breaking on safe (ink-free) rows.
// Returns [{ dataUrl, wPx, hPx }] with VARIABLE hPx (each ≤ one A4 page). reportPackage.js top-aligns each on
// a full A4 page, so a short page just leaves white below — no stretching. JPEG (0.9) keeps a text page small.
export async function bodyToPageImages(element, { scale = 2 } = {}) {
  if (!element) return []
  const html2canvas = (await import('html2canvas')).default   // lazy: loaded only on generate
  const full = await html2canvas(element, {
    scale,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  })
  const W = full.width, H = full.height
  const ctx = full.getContext('2d')
  // Max page height in px — this many rows map to exactly one A4 page height. A slice is never taller than
  // this, so it can never overflow the A4 page it is drawn onto.
  const budget = Math.max(1, Math.floor(W * (A4.h / A4.w)))

  // Classify every pixel row as a SAFE cut line = it holds no DARK pixels. Text glyphs and the #2b2b2b section
  // headers are dark; page background and light table-cell borders (#ccc) are not. Cutting on a safe row lands
  // in whitespace / on a thin border between rows — never through a glyph, a table row, or a dark header.
  const DARK = 110                                   // luminance below this counts as ink
  const TOL = Math.max(2, Math.floor(W * 0.004))     // tolerate a few anti-aliased stray pixels per row
  let px = null
  try { px = ctx.getImageData(0, 0, W, H).data } catch (e) { px = null }   // tainted-canvas guard
  const safe = new Uint8Array(H)
  if (px) {
    for (let y = 0; y < H; y++) {
      let dark = 0
      const base = y * W * 4
      for (let x = 0; x < W; x++) {
        const i = base + x * 4
        const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]
        if (lum < DARK && ++dark > TOL) break
      }
      safe[y] = dark <= TOL ? 1 : 0
    }
  }

  // Break each page at the LAST safe row at/above the ideal page end, searching up but not past ~60% of a page
  // (so pages don't get needlessly short). Falls back to a hard cut only if a stretch of content taller than a
  // page has no safe row at all (very rare — e.g. one oversized cell); that is no worse than the old behaviour.
  const cutFor = (top) => {
    const ideal = top + budget
    if (ideal >= H) return H
    const floorY = top + Math.floor(budget * 0.6)
    if (px) for (let r = ideal; r >= floorY; r--) if (safe[r]) return r
    return ideal
  }

  const pages = []
  for (let y = 0; y < H;) {
    let cut = cutFor(y)
    if (cut <= y) cut = Math.min(H, y + budget)   // defensive: never stall
    const h = cut - y
    const c = document.createElement('canvas')
    c.width = W; c.height = h
    const cc = c.getContext('2d')
    cc.fillStyle = '#ffffff'; cc.fillRect(0, 0, W, h)          // opaque white base (JPEG has no alpha)
    cc.drawImage(full, 0, y, W, h, 0, 0, W, h)
    pages.push({ dataUrl: c.toDataURL('image/jpeg', 0.9), wPx: W, hPx: h })
    y = cut
  }
  return pages
}
